import { Spinner } from '@zrsystem/ui';
import { Suspense, lazy } from 'react';
import { createBrowserRouter, Navigate, Outlet, useParams } from 'react-router-dom';

import { AuthenticatedLayout } from '@/app/layout/authenticated-layout.js';
import { NavigationLevelGuard } from '@/app/navigation-context/navigation-level-guard.js';
import { RouterBoundProviders } from '@/app/providers/router-bound-providers.js';
import { RequireAuth } from '@/app/router/guards/require-auth.guard.js';
import { RequireCompanyFinance } from '@/app/router/guards/require-company-finance.guard.js';
import { RequireDesignSystemPreview } from '@/app/router/guards/require-design-system.guard.js';
import { RequireFiscalView } from '@/app/router/guards/require-fiscal-view.guard.js';
import { RequireGeoCityCatalog } from '@/app/router/guards/require-geo-city-catalog.guard.js';
import { RequireOrganizationAccess } from '@/app/router/guards/require-organization-access.guard.js';
import { RequireOrganizationSettings } from '@/app/router/guards/require-organization-settings.guard.js';
import { RequirePlaceConfigView } from '@/app/router/guards/require-place-config-view.guard.js';
import { RedirectIfAuthenticated } from '@/app/router/guards/require-place.guard.js';
import { appPaths } from '@/app/router/paths.js';
import { PlaceLevelOutlet } from '@/app/router/place-level-outlet.js';
import { RedirectLegacyDesignSystem } from '@/app/router/redirects/dev-harness.redirects.js';

const LoginScreen = lazy(() =>
  import('@/features/auth/components/login-screen.js').then((module) => ({
    default: module.LoginScreen,
  })),
);

const AcceptInviteScreen = lazy(() =>
  import('@/features/auth/components/accept-invite-screen.js').then((module) => ({
    default: module.AcceptInviteScreen,
  })),
);

const CustomerPortalPage = lazy(() =>
  import('@/features/service-order/components/customer-portal-page.js').then((module) => ({
    default: module.CustomerPortalPage,
  })),
);

const StaffResetPasswordScreen = lazy(() =>
  import('@/features/auth/components/staff-reset-password-screen.js').then((module) => ({
    default: module.StaffResetPasswordScreen,
  })),
);

const HomeScreen = lazy(() =>
  import('@/features/dashboard/components/home-screen.js').then((module) => ({
    default: module.HomeScreen,
  })),
);

const CustomersListScreen = lazy(() =>
  import('@/features/customer/screens/customers-list.screen.js').then((module) => ({
    default: module.CustomersListScreen,
  })),
);

const StaffChatInboxScreen = lazy(() =>
  import('@/features/conversation/screens/staff-chat-inbox.screen.js').then((module) => ({
    default: module.StaffChatInboxScreen,
  })),
);

const WhatsAppInboxScreen = lazy(() =>
  import('@/features/notification/screens/whatsapp-inbox.screen.js').then((module) => ({
    default: module.WhatsAppInboxScreen,
  })),
);

const PlaceAppointmentsListScreen = lazy(() =>
  import('@/features/appointment/screens/place-appointments-list.screen.js').then((module) => ({
    default: module.PlaceAppointmentsListScreen,
  })),
);

const YardBoardScreen = lazy(() =>
  import('@/features/yard/screens/yard-board.screen.js').then((module) => ({
    default: module.YardBoardScreen,
  })),
);

const WorkTimerOvertimeConfirmScreen = lazy(() =>
  import('@/features/yard/screens/work-timer-overtime-confirm.screen.js').then((module) => ({
    default: module.WorkTimerOvertimeConfirmScreen,
  })),
);

const PlaceNotificationCampaignsScreen = lazy(() =>
  import('@/features/notification/screens/place-notification-campaigns.screen.js').then(
    (module) => ({
      default: module.PlaceNotificationCampaignsScreen,
    }),
  ),
);

const SuppliersListScreen = lazy(() =>
  import('@/features/supplier/screens/suppliers-list.screen.js').then((module) => ({
    default: module.SuppliersListScreen,
  })),
);

const SupplierDetailScreen = lazy(() =>
  import('@/features/supplier/screens/supplier-detail.screen.js').then((module) => ({
    default: module.SupplierDetailScreen,
  })),
);

const CustomerDetailScreen = lazy(() =>
  import('@/features/customer/screens/customer-detail.screen.js').then((module) => ({
    default: module.CustomerDetailScreen,
  })),
);

const VehicleDetailScreen = lazy(() =>
  import('@/features/vehicle/screens/vehicle-detail.screen.js').then((module) => ({
    default: module.VehicleDetailScreen,
  })),
);

const VehiclesListScreen = lazy(() =>
  import('@/features/vehicle/screens/vehicles-list.screen.js').then((module) => ({
    default: module.VehiclesListScreen,
  })),
);

const DesignSystemPage = lazy(() =>
  import('@/features/design-system/screens/design-system.page.js').then((module) => ({
    default: module.DesignSystemPage,
  })),
);

const CatalogHubScreen = lazy(() =>
  import('@/features/catalog/screens/catalog-hub.screen.js').then((module) => ({
    default: module.CatalogHubScreen,
  })),
);

const ServicesListScreen = lazy(() =>
  import('@/features/catalog/screens/services-list.screen.js').then((module) => ({
    default: module.ServicesListScreen,
  })),
);

const ProductsListScreen = lazy(() =>
  import('@/features/catalog/screens/products-list.screen.js').then((module) => ({
    default: module.ProductsListScreen,
  })),
);

const KitsListScreen = lazy(() =>
  import('@/features/catalog/screens/kits-list.screen.js').then((module) => ({
    default: module.KitsListScreen,
  })),
);

const UsefulLifeListScreen = lazy(() =>
  import('@/features/catalog/screens/useful-life-list.screen.js').then((module) => ({
    default: module.UsefulLifeListScreen,
  })),
);

const KitDetailScreen = lazy(() =>
  import('@/features/catalog/screens/kit-detail.screen.js').then((module) => ({
    default: module.KitDetailScreen,
  })),
);

const RevisionPlansListScreen = lazy(() =>
  import('@/features/catalog/screens/revision-plans-list.screen.js').then((module) => ({
    default: module.RevisionPlansListScreen,
  })),
);

const MaintenanceChecklistsListScreen = lazy(() =>
  import('@/features/catalog/screens/maintenance-checklists-list.screen.js').then((module) => ({
    default: module.MaintenanceChecklistsListScreen,
  })),
);

const DefaultPricePolicyScreen = lazy(() =>
  import('@/features/catalog/screens/default-price-policy.screen.js').then((module) => ({
    default: module.DefaultPricePolicyScreen,
  })),
);

const PriceSimulatorListScreen = lazy(() =>
  import('@/features/catalog/screens/price-simulator-list.screen.js').then((module) => ({
    default: module.PriceSimulatorListScreen,
  })),
);

const PricePromotionsListScreen = lazy(() =>
  import('@/features/catalog/screens/price-promotions-list.screen.js').then((module) => ({
    default: module.PricePromotionsListScreen,
  })),
);

const OrgDashboardScreen = lazy(() =>
  import('@/features/dashboard/screens/org-dashboard.screen.js').then((module) => ({
    default: module.OrgDashboardScreen,
  })),
);

const NetworkStubScreen = lazy(() =>
  import('@/app/navigation-context/screens/network-stub.screen.js').then((module) => ({
    default: module.NetworkStubScreen,
  })),
);

const OrganizationsListScreen = lazy(() =>
  import('@/features/platform/screens/organizations-list.screen.js').then((module) => ({
    default: module.OrganizationsListScreen,
  })),
);

const OrganizationDetailScreen = lazy(() =>
  import('@/features/platform/screens/organization-detail.screen.js').then((module) => ({
    default: module.OrganizationDetailScreen,
  })),
);

const PlacesListScreen = lazy(() =>
  import('@/features/platform/screens/places-list.screen.js').then((module) => ({
    default: module.PlacesListScreen,
  })),
);

const PlaceDetailScreen = lazy(() =>
  import('@/features/platform/screens/place-detail.screen.js').then((module) => ({
    default: module.PlaceDetailScreen,
  })),
);

const NetworksListScreen = lazy(() =>
  import('@/features/platform/screens/networks-list.screen.js').then((module) => ({
    default: module.NetworksListScreen,
  })),
);

const NetworkDetailScreen = lazy(() =>
  import('@/features/platform/screens/network-detail.screen.js').then((module) => ({
    default: module.NetworkDetailScreen,
  })),
);

const CatalogGlobalListScreen = lazy(() =>
  import('@/features/platform/screens/catalog-global-list.screen.js').then((module) => ({
    default: module.CatalogGlobalListScreen,
  })),
);

const CatalogCurationScreen = lazy(() =>
  import('@/features/platform/screens/catalog-curation.screen.js').then((module) => ({
    default: module.CatalogCurationScreen,
  })),
);

const CatalogTaxonomyListScreen = lazy(() =>
  import('@/features/platform/screens/catalog-taxonomy-list.screen.js').then((module) => ({
    default: module.CatalogTaxonomyListScreen,
  })),
);

const CatalogGenericProductListScreen = lazy(() =>
  import('@/features/platform/screens/catalog-generic-product-list.screen.js').then((module) => ({
    default: module.CatalogGenericProductListScreen,
  })),
);

const CatalogGenericServiceListScreen = lazy(() =>
  import('@/features/platform/screens/catalog-generic-service-list.screen.js').then((module) => ({
    default: module.CatalogGenericServiceListScreen,
  })),
);

const CatalogManufacturerListScreen = lazy(() =>
  import('@/features/platform/screens/catalog-manufacturer-list.screen.js').then((module) => ({
    default: module.CatalogManufacturerListScreen,
  })),
);

const UsersListScreen = lazy(() =>
  import('@/features/platform/screens/users-list.screen.js').then((module) => ({
    default: module.UsersListScreen,
  })),
);

const ProfilesListScreen = lazy(() =>
  import('@/features/platform/screens/profile-templates-list.screen.js').then((module) => ({
    default: module.ProfileTemplatesListScreen,
  })),
);

const ProfileTemplateDetailScreen = lazy(() =>
  import('@/features/platform/screens/profile-template-detail.screen.js').then((module) => ({
    default: module.ProfileTemplateDetailScreen,
  })),
);

const OrganizationUsersScreen = lazy(() =>
  import('@/features/platform/screens/organization-users.screen.js').then((module) => ({
    default: module.OrganizationUsersScreen,
  })),
);

const OrganizationProfilesScreen = lazy(() =>
  import('@/features/platform/screens/organization-profiles.screen.js').then((module) => ({
    default: module.OrganizationProfilesScreen,
  })),
);

const OrganizationProfileDetailScreen = lazy(() =>
  import('@/features/platform/screens/organization-profile-detail.screen.js').then((module) => ({
    default: module.OrganizationProfileDetailScreen,
  })),
);

const TaxonomiesRedirect = () => <Navigate to={appPaths.catalogServices} replace />;

const ProductDetailScreen = lazy(() =>
  import('@/features/catalog/screens/product-detail.screen.js').then((module) => ({
    default: module.ProductDetailScreen,
  })),
);

const InventoryHubScreen = lazy(() =>
  import('@/features/inventory/screens/inventory-hub.screen.js').then((module) => ({
    default: module.InventoryHubScreen,
  })),
);

const StockBalancesListScreen = lazy(() =>
  import('@/features/inventory/screens/stock-balances-list.screen.js').then((module) => ({
    default: module.StockBalancesListScreen,
  })),
);

const StockMovementsListScreen = lazy(() =>
  import('@/features/inventory/screens/stock-movements-list.screen.js').then((module) => ({
    default: module.StockMovementsListScreen,
  })),
);

const StockTransfersListScreen = lazy(() =>
  import('@/features/inventory/screens/stock-transfers-list.screen.js').then((module) => ({
    default: module.StockTransfersListScreen,
  })),
);

const StockInventoriesListScreen = lazy(() =>
  import('@/features/inventory/screens/stock-inventories-list.screen.js').then((module) => ({
    default: module.StockInventoriesListScreen,
  })),
);

const InboundConferenceListScreen = lazy(() =>
  import('@/features/inventory/screens/inbound-conference-list.screen.js').then((module) => ({
    default: module.InboundConferenceListScreen,
  })),
);

const InboundConferenceDetailScreen = lazy(() =>
  import('@/features/inventory/screens/inbound-conference-detail.screen.js').then((module) => ({
    default: module.InboundConferenceDetailScreen,
  })),
);

const PlaceSettingsScreen = lazy(() =>
  import('@/features/identity/screens/place-settings.screen.js').then((module) => ({
    default: module.PlaceSettingsScreen,
  })),
);

const WorkshopsListScreen = lazy(() =>
  import('@/features/identity/screens/workshops-list.screen.js').then((module) => ({
    default: module.WorkshopsListScreen,
  })),
);

const OrganizationSettingsScreen = lazy(() =>
  import('@/features/organization-settings/screens/organization-settings.screen.js').then(
    (module) => ({
      default: module.OrganizationSettingsScreen,
    }),
  ),
);
const GeoCityListScreen = lazy(() =>
  import('@/features/identity/screens/geo-city-list.screen.js').then((module) => ({
    default: module.GeoCityListScreen,
  })),
);

const FiscalParametrizacaoScreen = lazy(() =>
  import('@/features/fiscal/screens/fiscal-parametrizacao.screen.js').then((module) => ({
    default: module.FiscalParametrizacaoScreen,
  })),
);
const FiscalImplantationGuideScreen = lazy(() =>
  import('@/features/fiscal/screens/fiscal-implantation-guide.screen.js').then((module) => ({
    default: module.FiscalImplantationGuideScreen,
  })),
);
const FiscalDocumentsListScreen = lazy(() =>
  import('@/features/fiscal/screens/fiscal-documents-list.screen.js').then((module) => ({
    default: module.FiscalDocumentsListScreen,
  })),
);
const FiscalDocumentDetailScreen = lazy(() =>
  import('@/features/fiscal/screens/fiscal-document-detail.screen.js').then((module) => ({
    default: module.FiscalDocumentDetailScreen,
  })),
);
const FiscalNcmListScreen = lazy(() =>
  import('@/features/fiscal/screens/fiscal-ncm-list.screen.js').then((module) => ({
    default: module.FiscalNcmListScreen,
  })),
);
const FiscalDfeInboxListScreen = lazy(() =>
  import('@/features/fiscal/screens/fiscal-dfe-inbox-list.screen.js').then((module) => ({
    default: module.FiscalDfeInboxListScreen,
  })),
);
const FiscalDfeInboxDetailScreen = lazy(() =>
  import('@/features/fiscal/screens/fiscal-dfe-inbox-detail.screen.js').then((module) => ({
    default: module.FiscalDfeInboxDetailScreen,
  })),
);

const PurchaseQuotesListScreen = lazy(() =>
  import('@/features/purchasing/screens/purchase-quotes-list.screen.js').then((module) => ({
    default: module.PurchaseQuotesListScreen,
  })),
);

const PurchaseQuoteDetailScreen = lazy(() =>
  import('@/features/purchasing/screens/purchase-quote-detail.screen.js').then((module) => ({
    default: module.PurchaseQuoteDetailScreen,
  })),
);

const PurchaseOrdersListScreen = lazy(() =>
  import('@/features/purchasing/screens/purchase-orders-list.screen.js').then((module) => ({
    default: module.PurchaseOrdersListScreen,
  })),
);

const PurchaseOrderDetailScreen = lazy(() =>
  import('@/features/purchasing/screens/purchase-order-detail.screen.js').then((module) => ({
    default: module.PurchaseOrderDetailScreen,
  })),
);

const ServiceQuotesListScreen = lazy(() =>
  import('@/features/service-order/screens/service-quotes-list.screen.js').then((module) => ({
    default: module.ServiceQuotesListScreen,
  })),
);

const OpenReceptionScreen = lazy(() =>
  import('@/features/service-order/screens/open-reception.screen.js').then((module) => ({
    default: module.OpenReceptionScreen,
  })),
);

const ReceptionsListScreen = lazy(() =>
  import('@/features/service-order/screens/receptions-list.screen.js').then((module) => ({
    default: module.ReceptionsListScreen,
  })),
);

const ServiceQuoteDetailScreen = lazy(() =>
  import('@/features/service-order/screens/service-quote-detail.screen.js').then((module) => ({
    default: module.ServiceQuoteDetailScreen,
  })),
);

const ServiceOrdersListScreen = lazy(() =>
  import('@/features/service-order/screens/service-orders-list.screen.js').then((module) => ({
    default: module.ServiceOrdersListScreen,
  })),
);

const RealizedMarginScreen = lazy(() =>
  import('@/features/service-order/screens/realized-margin.screen.js').then((module) => ({
    default: module.RealizedMarginScreen,
  })),
);

const BreakEvenScreen = lazy(() =>
  import('@/features/service-order/screens/break-even.screen.js').then((module) => ({
    default: module.BreakEvenScreen,
  })),
);

const ProductivityScreen = lazy(() =>
  import('@/features/service-order/screens/productivity.screen.js').then((module) => ({
    default: module.ProductivityScreen,
  })),
);

const ServiceOrderDetailScreen = lazy(() =>
  import('@/features/service-order/screens/service-order-detail.screen.js').then((module) => ({
    default: module.ServiceOrderDetailScreen,
  })),
);

const ServiceOrderCheckoutScreen = lazy(() =>
  import('@/features/service-order/screens/service-order-checkout.screen.js').then((module) => ({
    default: module.ServiceOrderCheckoutScreen,
  })),
);

const CheckOutListScreen = lazy(() =>
  import('@/features/service-order/screens/check-out-list.screen.js').then((module) => ({
    default: module.CheckOutListScreen,
  })),
);

const CaixaScreen = lazy(() =>
  import('@/features/finance/screens/caixa.screen.js').then((module) => ({
    default: module.CaixaScreen,
  })),
);

const ChartOfAccountsScreen = lazy(() =>
  import('@/features/finance/screens/chart-of-accounts.screen.js').then((module) => ({
    default: module.ChartOfAccountsScreen,
  })),
);

const InboundChargePolicyScreen = lazy(() =>
  import('@/features/finance/screens/inbound-charge-policy.screen.js').then((module) => ({
    default: module.InboundChargePolicyScreen,
  })),
);

const AccountsPayableScreen = lazy(() =>
  import('@/features/finance/screens/accounts-payable.screen.js').then((module) => ({
    default: module.AccountsPayableScreen,
  })),
);

const AccountsReceivableScreen = lazy(() =>
  import('@/features/finance/screens/accounts-receivable.screen.js').then((module) => ({
    default: module.AccountsReceivableScreen,
  })),
);

const CashFlowScreen = lazy(() =>
  import('@/features/finance/screens/cash-flow.screen.js').then((module) => ({
    default: module.CashFlowScreen,
  })),
);

const IncomeStatementScreen = lazy(() =>
  import('@/features/finance/screens/income-statement.screen.js').then((module) => ({
    default: module.IncomeStatementScreen,
  })),
);

const PaymentSettingsScreen = lazy(() =>
  import('@/features/finance/screens/payment-settings.screen.js').then((module) => ({
    default: module.PaymentSettingsScreen,
  })),
);

const CollectionChargesScreen = lazy(() =>
  import('@/features/finance/screens/collection-charges.screen.js').then((module) => ({
    default: module.CollectionChargesScreen,
  })),
);

const BankReconciliationScreen = lazy(() =>
  import('@/features/finance/screens/bank-reconciliation.screen.js').then((module) => ({
    default: module.BankReconciliationScreen,
  })),
);

const ServiceDetailScreen = lazy(() =>
  import('@/features/catalog/screens/service-detail.screen.js').then((module) => ({
    default: module.ServiceDetailScreen,
  })),
);

function RouteFallback() {
  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '40vh' }}>
      <Spinner aria-label="Carregando página" />
    </div>
  );
}

function RedirectLegacyVehicleDetail() {
  const { customerVehicleId = '' } = useParams();
  return <Navigate to={appPaths.vehicleDetail(customerVehicleId)} replace />;
}

function AdminLevelOutlet() {
  return (
    <NavigationLevelGuard>
      <Suspense fallback={<RouteFallback />}>
        <Outlet />
      </Suspense>
    </NavigationLevelGuard>
  );
}

export const appRouter = createBrowserRouter([
  {
    element: (
      <RouterBoundProviders>
        <Outlet />
      </RouterBoundProviders>
    ),
    children: [
      {
        path: appPaths.login,
        element: (
          <RedirectIfAuthenticated>
            <Suspense fallback={<RouteFallback />}>
              <LoginScreen />
            </Suspense>
          </RedirectIfAuthenticated>
        ),
      },
      {
        path: appPaths.customerPortal,
        element: (
          <Suspense fallback={<RouteFallback />}>
            <CustomerPortalPage />
          </Suspense>
        ),
      },
      {
        path: appPaths.acceptInvite,
        element: (
          <Suspense fallback={<RouteFallback />}>
            <AcceptInviteScreen />
          </Suspense>
        ),
      },
      {
        path: appPaths.staffResetPassword,
        element: (
          <Suspense fallback={<RouteFallback />}>
            <StaffResetPasswordScreen />
          </Suspense>
        ),
      },
      {
        path: appPaths.home,
        element: (
          <RequireAuth>
            <AuthenticatedLayout />
          </RequireAuth>
        ),
        children: [
          {
            element: <PlaceLevelOutlet />,
            handle: { navigationLevel: 'place' },
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<RouteFallback />}>
                    <HomeScreen />
                  </Suspense>
                ),
              },
              {
                path: 'customers',
                element: (
                  <Suspense fallback={<RouteFallback />}>
                    <CustomersListScreen />
                  </Suspense>
                ),
              },
              {
                path: 'customers/:partyId',
                element: (
                  <Suspense fallback={<RouteFallback />}>
                    <CustomerDetailScreen />
                  </Suspense>
                ),
              },
              {
                path: 'conversations',
                element: (
                  <Suspense fallback={<RouteFallback />}>
                    <StaffChatInboxScreen />
                  </Suspense>
                ),
              },
              {
                path: 'whatsapp',
                element: (
                  <Suspense fallback={<RouteFallback />}>
                    <WhatsAppInboxScreen />
                  </Suspense>
                ),
              },
              {
                path: 'agenda',
                element: (
                  <Suspense fallback={<RouteFallback />}>
                    <PlaceAppointmentsListScreen />
                  </Suspense>
                ),
              },
              {
                path: 'patio',
                element: (
                  <Suspense fallback={<RouteFallback />}>
                    <YardBoardScreen />
                  </Suspense>
                ),
              },
              {
                path: 'patio/hora-extra/:mechanicUserId',
                element: (
                  <Suspense fallback={<RouteFallback />}>
                    <WorkTimerOvertimeConfirmScreen />
                  </Suspense>
                ),
              },
              {
                path: 'campanhas',
                element: (
                  <Suspense fallback={<RouteFallback />}>
                    <PlaceNotificationCampaignsScreen />
                  </Suspense>
                ),
              },
              {
                path: 'campanhas/:campaignId',
                element: <Navigate to={appPaths.notificationCampaigns} replace />,
              },
              {
                path: 'suppliers',
                element: (
                  <Suspense fallback={<RouteFallback />}>
                    <SuppliersListScreen />
                  </Suspense>
                ),
              },
              {
                path: 'suppliers/:supplierRoleId',
                element: (
                  <Suspense fallback={<RouteFallback />}>
                    <SupplierDetailScreen />
                  </Suspense>
                ),
              },
              {
                path: 'vehicles',
                element: (
                  <Suspense fallback={<RouteFallback />}>
                    <VehiclesListScreen />
                  </Suspense>
                ),
              },
              {
                path: 'vehicles/:customerVehicleId',
                element: (
                  <Suspense fallback={<RouteFallback />}>
                    <VehicleDetailScreen />
                  </Suspense>
                ),
              },
              {
                path: 'customers/:partyId/vehicles/:customerVehicleId',
                element: <RedirectLegacyVehicleDetail />,
              },
              {
                path: 'catalog',
                element: (
                  <Suspense fallback={<RouteFallback />}>
                    <CatalogHubScreen />
                  </Suspense>
                ),
                children: [
                  {
                    index: true,
                    element: <Navigate to={appPaths.catalogServices} replace />,
                  },
                  {
                    path: 'services/:serviceId',
                    element: (
                      <Suspense fallback={<RouteFallback />}>
                        <ServiceDetailScreen />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'services',
                    element: (
                      <Suspense fallback={<RouteFallback />}>
                        <ServicesListScreen />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'products/:productId',
                    element: (
                      <Suspense fallback={<RouteFallback />}>
                        <ProductDetailScreen />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'products',
                    element: (
                      <Suspense fallback={<RouteFallback />}>
                        <ProductsListScreen />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'kits/:kitId',
                    element: (
                      <Suspense fallback={<RouteFallback />}>
                        <KitDetailScreen />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'kits',
                    element: (
                      <Suspense fallback={<RouteFallback />}>
                        <KitsListScreen />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'useful-life',
                    element: (
                      <Suspense fallback={<RouteFallback />}>
                        <UsefulLifeListScreen />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'revision-plans',
                    element: (
                      <Suspense fallback={<RouteFallback />}>
                        <RevisionPlansListScreen />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'maintenance-checklists',
                    element: (
                      <Suspense fallback={<RouteFallback />}>
                        <MaintenanceChecklistsListScreen />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'price-policy',
                    element: (
                      <Suspense fallback={<RouteFallback />}>
                        <DefaultPricePolicyScreen />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'price-simulator',
                    element: (
                      <Suspense fallback={<RouteFallback />}>
                        <PriceSimulatorListScreen />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'price-promotions',
                    element: (
                      <Suspense fallback={<RouteFallback />}>
                        <PricePromotionsListScreen />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'taxonomies',
                    element: <TaxonomiesRedirect />,
                  },
                ],
              },
              {
                path: 'inventory',
                element: (
                  <Suspense fallback={<RouteFallback />}>
                    <InventoryHubScreen />
                  </Suspense>
                ),
                children: [
                  {
                    index: true,
                    element: <Navigate to={appPaths.inventoryStockBalances} replace />,
                  },
                  {
                    path: 'stock-balances',
                    element: (
                      <Suspense fallback={<RouteFallback />}>
                        <StockBalancesListScreen />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'stock-movements',
                    element: (
                      <Suspense fallback={<RouteFallback />}>
                        <StockMovementsListScreen />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'stock-transfers',
                    element: (
                      <Suspense fallback={<RouteFallback />}>
                        <StockTransfersListScreen />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'stock-inventories',
                    element: (
                      <Suspense fallback={<RouteFallback />}>
                        <StockInventoriesListScreen />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'conferencia-entrada',
                    element: (
                      <Suspense fallback={<RouteFallback />}>
                        <InboundConferenceListScreen />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'conferencia-entrada/:conferenceId',
                    element: (
                      <Suspense fallback={<RouteFallback />}>
                        <InboundConferenceDetailScreen />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                path: 'purchasing',
                children: [
                  {
                    index: true,
                    element: <Navigate to={appPaths.purchasingQuotes} replace />,
                  },
                  {
                    path: 'quotes',
                    element: (
                      <Suspense fallback={<RouteFallback />}>
                        <PurchaseQuotesListScreen />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'quotes/:purchaseQuoteId',
                    element: (
                      <Suspense fallback={<RouteFallback />}>
                        <PurchaseQuoteDetailScreen />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'orders',
                    element: (
                      <Suspense fallback={<RouteFallback />}>
                        <PurchaseOrdersListScreen />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'orders/:purchaseOrderId',
                    element: (
                      <Suspense fallback={<RouteFallback />}>
                        <PurchaseOrderDetailScreen />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                path: 'check-in',
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<RouteFallback />}>
                        <ReceptionsListScreen />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'new',
                    element: (
                      <Suspense fallback={<RouteFallback />}>
                        <OpenReceptionScreen />
                      </Suspense>
                    ),
                  },
                  {
                    path: ':receptionId',
                    element: (
                      <Suspense fallback={<RouteFallback />}>
                        <OpenReceptionScreen />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                path: 'quotes',
                element: (
                  <Suspense fallback={<RouteFallback />}>
                    <ServiceQuotesListScreen />
                  </Suspense>
                ),
              },
              {
                path: 'quotes/:quoteId',
                element: (
                  <Suspense fallback={<RouteFallback />}>
                    <ServiceQuoteDetailScreen />
                  </Suspense>
                ),
              },
              {
                path: 'service-orders',
                element: (
                  <Suspense fallback={<RouteFallback />}>
                    <ServiceOrdersListScreen />
                  </Suspense>
                ),
              },
              {
                path: 'service-orders/realized-margin',
                element: (
                  <Suspense fallback={<RouteFallback />}>
                    <RealizedMarginScreen />
                  </Suspense>
                ),
              },
              {
                path: 'service-orders/break-even',
                element: (
                  <Suspense fallback={<RouteFallback />}>
                    <BreakEvenScreen />
                  </Suspense>
                ),
              },
              {
                path: 'service-orders/productivity',
                element: (
                  <Suspense fallback={<RouteFallback />}>
                    <ProductivityScreen />
                  </Suspense>
                ),
              },
              {
                path: 'service-orders/:serviceOrderId',
                element: (
                  <Suspense fallback={<RouteFallback />}>
                    <ServiceOrderDetailScreen />
                  </Suspense>
                ),
              },
              {
                path: 'configuracoes',
                element: <Navigate to={appPaths.home} replace />,
              },
              {
                path: 'cidades',
                handle: { navigationLevel: 'place' },
                element: (
                  <RequireGeoCityCatalog>
                    <Suspense fallback={<RouteFallback />}>
                      <GeoCityListScreen />
                    </Suspense>
                  </RequireGeoCityCatalog>
                ),
              },
              {
                path: 'check-out',
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<RouteFallback />}>
                        <CheckOutListScreen />
                      </Suspense>
                    ),
                  },
                  {
                    path: ':serviceOrderId',
                    element: (
                      <Suspense fallback={<RouteFallback />}>
                        <ServiceOrderCheckoutScreen />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                path: 'caixa',
                element: (
                  <Suspense fallback={<RouteFallback />}>
                    <CaixaScreen />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: 'financeiro/plano-de-contas',
            handle: { navigationLevel: 'tenant' },
            element: (
              <RequireCompanyFinance>
                <Suspense fallback={<RouteFallback />}>
                  <ChartOfAccountsScreen />
                </Suspense>
              </RequireCompanyFinance>
            ),
          },
          {
            path: 'financeiro/contas-a-pagar',
            handle: { navigationLevel: 'tenant' },
            element: (
              <Suspense fallback={<RouteFallback />}>
                <AccountsPayableScreen />
              </Suspense>
            ),
          },
          {
            path: 'financeiro/contas-a-receber',
            handle: { navigationLevel: 'tenant' },
            element: (
              <Suspense fallback={<RouteFallback />}>
                <AccountsReceivableScreen />
              </Suspense>
            ),
          },
          {
            path: 'financeiro/fluxo-de-caixa',
            handle: { navigationLevel: 'tenant' },
            element: (
              <Suspense fallback={<RouteFallback />}>
                <CashFlowScreen />
              </Suspense>
            ),
          },
          {
            path: 'financeiro/dre',
            handle: { navigationLevel: 'tenant' },
            element: (
              <RequireCompanyFinance>
                <Suspense fallback={<RouteFallback />}>
                  <IncomeStatementScreen />
                </Suspense>
              </RequireCompanyFinance>
            ),
          },
          {
            path: 'financeiro/encargos-entrada',
            handle: { navigationLevel: 'tenant' },
            element: (
              <RequireCompanyFinance>
                <Suspense fallback={<RouteFallback />}>
                  <InboundChargePolicyScreen />
                </Suspense>
              </RequireCompanyFinance>
            ),
          },
          {
            path: 'financeiro/pagamentos',
            handle: { navigationLevel: 'tenant' },
            element: (
              <Suspense fallback={<RouteFallback />}>
                <PaymentSettingsScreen />
              </Suspense>
            ),
          },
          {
            path: 'financeiro/cobrancas',
            handle: { navigationLevel: 'tenant' },
            element: (
              <RequireCompanyFinance>
                <Suspense fallback={<RouteFallback />}>
                  <CollectionChargesScreen />
                </Suspense>
              </RequireCompanyFinance>
            ),
          },
          {
            path: 'financeiro/conciliacao',
            handle: { navigationLevel: 'place' },
            element: (
              <RequireCompanyFinance>
                <Suspense fallback={<RouteFallback />}>
                  <BankReconciliationScreen />
                </Suspense>
              </RequireCompanyFinance>
            ),
          },
          {
            path: 'fiscal/parametrizacao',
            handle: { navigationLevel: 'tenant' },
            element: (
              <RequireFiscalView>
                <Suspense fallback={<RouteFallback />}>
                  <FiscalParametrizacaoScreen />
                </Suspense>
              </RequireFiscalView>
            ),
          },
          {
            path: 'fiscal/implantacao',
            handle: { navigationLevel: 'tenant' },
            element: (
              <RequireFiscalView>
                <Suspense fallback={<RouteFallback />}>
                  <FiscalImplantationGuideScreen />
                </Suspense>
              </RequireFiscalView>
            ),
          },
          {
            path: 'fiscal/documentos',
            handle: { navigationLevel: 'tenant' },
            element: (
              <RequireFiscalView>
                <Suspense fallback={<RouteFallback />}>
                  <FiscalDocumentsListScreen />
                </Suspense>
              </RequireFiscalView>
            ),
          },
          {
            path: 'fiscal/documentos/:documentId',
            handle: { navigationLevel: 'tenant' },
            element: (
              <RequireFiscalView>
                <Suspense fallback={<RouteFallback />}>
                  <FiscalDocumentDetailScreen />
                </Suspense>
              </RequireFiscalView>
            ),
          },
          {
            path: 'fiscal/caixa-entrada',
            handle: { navigationLevel: 'tenant' },
            element: (
              <RequireFiscalView>
                <Suspense fallback={<RouteFallback />}>
                  <FiscalDfeInboxListScreen />
                </Suspense>
              </RequireFiscalView>
            ),
          },
          {
            path: 'fiscal/caixa-entrada/:documentId',
            handle: { navigationLevel: 'tenant' },
            element: (
              <RequireFiscalView>
                <Suspense fallback={<RouteFallback />}>
                  <FiscalDfeInboxDetailScreen />
                </Suspense>
              </RequireFiscalView>
            ),
          },
          {
            path: 'fiscal/ncm',
            handle: { navigationLevel: 'tenant' },
            element: (
              <RequireFiscalView>
                <Suspense fallback={<RouteFallback />}>
                  <FiscalNcmListScreen />
                </Suspense>
              </RequireFiscalView>
            ),
          },
          {
            path: 'org',
            handle: { navigationLevel: 'tenant' },
            element: (
              <NavigationLevelGuard>
                <Suspense fallback={<RouteFallback />}>
                  <OrgDashboardScreen />
                </Suspense>
              </NavigationLevelGuard>
            ),
          },
          {
            path: 'organizacao/clientes',
            handle: { navigationLevel: 'tenant' },
            element: (
              <Suspense fallback={<RouteFallback />}>
                <CustomersListScreen />
              </Suspense>
            ),
          },
          {
            path: 'organizacao/clientes/:partyId',
            handle: { navigationLevel: 'tenant' },
            element: (
              <Suspense fallback={<RouteFallback />}>
                <CustomerDetailScreen />
              </Suspense>
            ),
          },
          {
            path: 'organizacao/catalogo/servicos',
            handle: { navigationLevel: 'tenant' },
            element: (
              <Suspense fallback={<RouteFallback />}>
                <ServicesListScreen />
              </Suspense>
            ),
          },
          {
            path: 'organizacao/catalogo/servicos/:serviceId',
            handle: { navigationLevel: 'tenant' },
            element: (
              <Suspense fallback={<RouteFallback />}>
                <ServiceDetailScreen />
              </Suspense>
            ),
          },
          {
            path: 'organizacao/catalogo/produtos',
            handle: { navigationLevel: 'tenant' },
            element: (
              <Suspense fallback={<RouteFallback />}>
                <ProductsListScreen />
              </Suspense>
            ),
          },
          {
            path: 'organizacao/catalogo/produtos/:productId',
            handle: { navigationLevel: 'tenant' },
            element: (
              <Suspense fallback={<RouteFallback />}>
                <ProductDetailScreen />
              </Suspense>
            ),
          },
          {
            path: 'organizacao/catalogo/kits',
            handle: { navigationLevel: 'tenant' },
            element: (
              <Suspense fallback={<RouteFallback />}>
                <KitsListScreen />
              </Suspense>
            ),
          },
          {
            path: 'organizacao/catalogo/kits/:kitId',
            handle: { navigationLevel: 'tenant' },
            element: (
              <Suspense fallback={<RouteFallback />}>
                <KitDetailScreen />
              </Suspense>
            ),
          },
          {
            path: 'organizacao/catalogo/vida-util',
            handle: { navigationLevel: 'tenant' },
            element: (
              <Suspense fallback={<RouteFallback />}>
                <UsefulLifeListScreen />
              </Suspense>
            ),
          },
          {
            path: 'organizacao/catalogo/planos-de-revisao',
            handle: { navigationLevel: 'tenant' },
            element: (
              <Suspense fallback={<RouteFallback />}>
                <RevisionPlansListScreen />
              </Suspense>
            ),
          },
          {
            path: 'organizacao/catalogo/check-lists',
            handle: { navigationLevel: 'tenant' },
            element: (
              <Suspense fallback={<RouteFallback />}>
                <MaintenanceChecklistsListScreen />
              </Suspense>
            ),
          },
          {
            path: 'organizacao/catalogo/precificacao',
            handle: { navigationLevel: 'tenant' },
            element: (
              <Suspense fallback={<RouteFallback />}>
                <DefaultPricePolicyScreen />
              </Suspense>
            ),
          },
          {
            path: 'organizacao/catalogo/simulador',
            handle: { navigationLevel: 'tenant' },
            element: (
              <Suspense fallback={<RouteFallback />}>
                <PriceSimulatorListScreen />
              </Suspense>
            ),
          },
          {
            path: 'organizacao/catalogo/promocoes',
            handle: { navigationLevel: 'tenant' },
            element: (
              <Suspense fallback={<RouteFallback />}>
                <PricePromotionsListScreen />
              </Suspense>
            ),
          },
          {
            path: 'organizacao/oficinas',
            handle: { navigationLevel: 'tenant' },
            element: (
              <NavigationLevelGuard>
                <Suspense fallback={<RouteFallback />}>
                  <WorkshopsListScreen />
                </Suspense>
              </NavigationLevelGuard>
            ),
          },
          {
            path: 'organizacao/oficinas/:placeId',
            handle: { navigationLevel: 'tenant' },
            element: (
              <NavigationLevelGuard>
                <RequirePlaceConfigView>
                  <Suspense fallback={<RouteFallback />}>
                    <PlaceSettingsScreen />
                  </Suspense>
                </RequirePlaceConfigView>
              </NavigationLevelGuard>
            ),
          },
          {
            path: 'organizacao/usuarios',
            handle: { navigationLevel: 'tenant' },
            element: (
              <RequireOrganizationAccess>
                <Suspense fallback={<RouteFallback />}>
                  <OrganizationUsersScreen />
                </Suspense>
              </RequireOrganizationAccess>
            ),
          },
          {
            path: 'organizacao/perfis',
            handle: { navigationLevel: 'tenant' },
            element: (
              <RequireOrganizationAccess>
                <Suspense fallback={<RouteFallback />}>
                  <OrganizationProfilesScreen />
                </Suspense>
              </RequireOrganizationAccess>
            ),
          },
          {
            path: 'organizacao/perfis/:profileId',
            handle: { navigationLevel: 'tenant' },
            element: (
              <RequireOrganizationAccess>
                <Suspense fallback={<RouteFallback />}>
                  <OrganizationProfileDetailScreen />
                </Suspense>
              </RequireOrganizationAccess>
            ),
          },
          {
            path: 'organizacao/configuracoes',
            handle: { navigationLevel: 'tenant' },
            element: (
              <RequireOrganizationSettings>
                <Suspense fallback={<RouteFallback />}>
                  <OrganizationSettingsScreen />
                </Suspense>
              </RequireOrganizationSettings>
            ),
          },
          {
            path: 'network/:organizationGroupId',
            handle: { navigationLevel: 'network' },
            element: (
              <NavigationLevelGuard>
                <Suspense fallback={<RouteFallback />}>
                  <NetworkStubScreen />
                </Suspense>
              </NavigationLevelGuard>
            ),
          },
          {
            path: 'admin',
            handle: { navigationLevel: 'platform' },
            element: <AdminLevelOutlet />,
            children: [
              {
                index: true,
                element: <Navigate to={appPaths.adminOrganizations} replace />,
              },
              {
                path: 'organizations',
                children: [
                  {
                    index: true,
                    element: <OrganizationsListScreen />,
                  },
                  {
                    path: ':organizationId',
                    element: <OrganizationDetailScreen />,
                  },
                ],
              },
              {
                path: 'places',
                children: [
                  {
                    index: true,
                    element: <PlacesListScreen />,
                  },
                  {
                    path: ':placeId',
                    element: <PlaceDetailScreen />,
                  },
                ],
              },
              {
                path: 'networks',
                children: [
                  {
                    index: true,
                    element: <NetworksListScreen />,
                  },
                  {
                    path: ':networkId',
                    element: <NetworkDetailScreen />,
                  },
                ],
              },
              {
                path: 'catalog-global',
                element: <CatalogGlobalListScreen />,
              },
              {
                path: 'catalog-curation',
                element: <CatalogCurationScreen />,
              },
              {
                path: 'catalog-taxonomy',
                element: <CatalogTaxonomyListScreen />,
              },
              {
                path: 'catalog-generic',
                element: <CatalogGenericProductListScreen />,
              },
              {
                path: 'catalog-generic-services',
                element: <CatalogGenericServiceListScreen />,
              },
              {
                path: 'catalog-manufacturers',
                element: <CatalogManufacturerListScreen />,
              },
              {
                path: 'cidades',
                element: (
                  <Suspense fallback={<RouteFallback />}>
                    <GeoCityListScreen />
                  </Suspense>
                ),
              },
              {
                path: 'users',
                element: <UsersListScreen />,
              },
              {
                path: 'profiles',
                element: <ProfilesListScreen />,
              },
              {
                path: 'profiles/:profileTemplateId',
                element: <ProfileTemplateDetailScreen />,
              },
            ],
          },
        ],
      },
      {
        path: appPaths.designSystemLegacy,
        element: <RedirectLegacyDesignSystem />,
      },
      {
        path: appPaths.harness,
        element: <Navigate to={appPaths.harnessDesignSystem} replace />,
      },
      {
        path: appPaths.harnessDesignSystem,
        element: (
          <RequireAuth>
            <RequireDesignSystemPreview>
              <Suspense fallback={<RouteFallback />}>
                <DesignSystemPage />
              </Suspense>
            </RequireDesignSystemPreview>
          </RequireAuth>
        ),
      },
      {
        path: '/harness/visual/*',
        element: <Navigate to={appPaths.harnessDesignSystem} replace />,
      },
      {
        path: '/harness/preview',
        element: <Navigate to={appPaths.harnessDesignSystem} replace />,
      },
      {
        path: '/harness/preview/:feId',
        element: <Navigate to={appPaths.harnessDesignSystem} replace />,
      },
      {
        path: '/harness/slices',
        element: <Navigate to={appPaths.harnessDesignSystem} replace />,
      },
      {
        path: '/harness/slices/:feId',
        element: <Navigate to={appPaths.harnessDesignSystem} replace />,
      },
      {
        path: '*',
        element: <Navigate to={appPaths.home} replace />,
      },
    ],
  },
]);
